// components/organisation/InvoicesSection.tsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2, MoreHorizontal, Plus } from '@/components/ui/lucide-memo';
import { useKernel } from '@/kernel/Kernel';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { formatCreditsInteger } from '#lib/formatCredits';
import type { InvoiceDTO, OrganisationPlanDTO } from '#types/types';

interface InvoicesSectionProps {
  organisationId: string;
  plans: OrganisationPlanDTO[];
  onPlansChanged: () => void;
}

// ChatGPT's billing list (owner, 2026-09-29): one row per invoice, its detail in one grey line,
// the amount on the right and the rarer actions behind '...'. Sizes pinned inline: the global
// clamp shrinks div, span and button text to 12px.
const TEXT_15: React.CSSProperties = { fontSize: 15 };
const TEXT_14: React.CSSProperties = { fontSize: 14, lineHeight: '20px' };
const inputClass =
  'h-10 w-full rounded-[12px] border border-[#2F2F2F] bg-transparent px-3 text-[#ECECEC] outline-none placeholder:text-[#8E8E8E] focus:border-[#8E8E8E] disabled:cursor-not-allowed disabled:opacity-50 [color-scheme:dark]';
const selectClass =
  'h-9 cursor-pointer rounded-full bg-transparent px-3 text-[#ECECEC] outline-none transition-colors hover:bg-[#212121] disabled:cursor-not-allowed disabled:opacity-45';
const secondaryButtonClass =
  'inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-full bg-[#212121] px-4 text-[#ECECEC] transition-colors hover:bg-[#2F2F2F] disabled:cursor-not-allowed disabled:opacity-45';
const ghostButtonClass =
  'inline-flex h-9 shrink-0 items-center justify-center rounded-full px-3 text-[#ECECEC] transition-colors hover:bg-[#212121]';
const primaryButtonClass =
  'inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-full bg-[#F0F0F0] px-4 font-medium text-black transition-colors hover:bg-white disabled:cursor-not-allowed disabled:opacity-40';

const formatMoney = (amount: number, currency: string) => {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
};

const formatDay = (iso: string) => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? 'Unknown date' : format(date, 'MMM d, yyyy');
};

const fetchInvoices = async (organisationId: string): Promise<InvoiceDTO[]> => {
  const response = await axios.post('/api/admin', { action: 'listInvoices', organisationId });
  return response.data?.invoices ?? [];
};

const InvoicesSection: React.FC<InvoicesSectionProps> = ({ organisationId, plans, onPlansChanged }) => {
  const { kernel } = useKernel();
  // Loaded invoices carry the organisation they belong to, so "still loading" is derived
  // rather than tracked: a stale tenant's list can never be painted as if it were current.
  const [loaded, setLoaded] = useState<{ organisationId: string; invoices: InvoiceDTO[] } | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [linkingPlanId, setLinkingPlanId] = useState<string | null>(null);
  const [busyInvoiceId, setBusyInvoiceId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editNumber, setEditNumber] = useState('');

  const [number, setNumber] = useState('');
  const [reference, setReference] = useState('');
  const [purchaseOrder, setPurchaseOrder] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [totalAmount, setTotalAmount] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [documentUrl, setDocumentUrl] = useState('');

  const invoices = loaded?.organisationId === organisationId ? loaded.invoices : [];
  const isLoading = loaded?.organisationId !== organisationId;

  const loadInvoices = useCallback(async () => {
    try {
      setLoaded({ organisationId, invoices: await fetchInvoices(organisationId) });
    } catch {
      toast.error('Could not load invoices.');
    }
  }, [organisationId]);

  useEffect(() => {
    let cancelled = false;
    fetchInvoices(organisationId)
      .then((result) => { if (!cancelled) setLoaded({ organisationId, invoices: result }); })
      .catch(() => { if (!cancelled) toast.error('Could not load invoices.'); });
    return () => { cancelled = true; };
  }, [organisationId]);

  const unlinkedGrants = useMemo(
    () => plans.filter((plan) => !plan.invoiceIds?.length),
    [plans],
  );

  const handleCreate = useCallback(async () => {
    const parsedTotal = Number.parseFloat(totalAmount);
    if (!number.trim()) {
      toast.error('Invoice number is required.');
      return;
    }
    if (!Number.isFinite(parsedTotal) || parsedTotal < 0) {
      toast.error('Invoice total must be a non-negative number.');
      return;
    }
    if (Number.isNaN(new Date(issueDate).getTime())) {
      toast.error('A valid issue date is required.');
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post('/api/admin', {
        action: 'createInvoice',
        organisationId,
        invoiceNumber: number.trim(),
        invoiceReference: reference.trim() || null,
        purchaseOrder: purchaseOrder.trim() || null,
        issueDate,
        dueDate: dueDate || null,
        totalAmount: parsedTotal,
        currency: currency.trim().toUpperCase(),
        documentUrl: documentUrl.trim() || null,
      });
      toast.success('Invoice recorded.');
      setIsFormOpen(false);
      setNumber('');
      setReference('');
      setPurchaseOrder('');
      setIssueDate('');
      setDueDate('');
      setTotalAmount('');
      setDocumentUrl('');
      await loadInvoices();
    } catch (error: unknown) {
      const message = axios.isAxiosError(error) ? String(error.response?.data ?? error.message) : 'Failed to record invoice.';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [number, reference, purchaseOrder, issueDate, dueDate, totalAmount, currency, documentUrl, organisationId, loadInvoices]);

  const handleUpload = useCallback(async (invoiceId: string, file: File) => {
    if (file.type !== 'application/pdf') {
      toast.error('Only PDF invoices are accepted.');
      return;
    }
    setBusyInvoiceId(invoiceId);
    try {
      // Read as base64 rather than multipart: the admin endpoint is a single JSON action
      // dispatcher, and an invoice PDF is small enough that the encoding overhead is noise.
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error('Could not read the file.'));
        reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
        reader.readAsDataURL(file);
      });
      await axios.post('/api/admin', { action: 'uploadInvoiceDocument', organisationId, invoiceId, documentBase64: base64, documentMimeType: file.type });
      toast.success('Invoice document attached.');
      await loadInvoices();
    } catch (error: unknown) {
      const message = axios.isAxiosError(error) ? String(error.response?.data ?? error.message) : 'Upload failed.';
      toast.error(message);
    } finally {
      setBusyInvoiceId(null);
    }
  }, [organisationId, loadInvoices]);

  const handleRename = useCallback(async (invoiceId: string) => {
    const next = editNumber.trim();
    if (!next) {
      toast.error('Invoice number cannot be blank.');
      return;
    }
    setBusyInvoiceId(invoiceId);
    try {
      await axios.post('/api/admin', { action: 'updateInvoice', organisationId, invoiceId, invoiceNumber: next });
      toast.success('Invoice number updated.');
      setEditingId(null);
      await loadInvoices();
    } catch (error: unknown) {
      const message = axios.isAxiosError(error) ? String(error.response?.data ?? error.message) : 'Could not update the invoice.';
      toast.error(message);
    } finally {
      setBusyInvoiceId(null);
    }
  }, [editNumber, organisationId, loadInvoices]);

  const handleLink = useCallback(async (planId: string, invoiceId: string) => {
    if (!invoiceId) return;
    setLinkingPlanId(planId);
    try {
      await axios.post('/api/admin', { action: 'linkPlanToInvoice', organisationId, planId, invoiceId });
      toast.success('Grant linked to invoice.');
      await loadInvoices();
      onPlansChanged();
    } catch {
      toast.error('Could not link the grant.');
    } finally {
      setLinkingPlanId(null);
    }
  }, [organisationId, loadInvoices, onPlansChanged]);

  const openInvoiceMenu = (event: React.MouseEvent<HTMLButtonElement>, invoice: InvoiceDTO) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    kernel.current?.setMenuData({
      position: { x: rect.right, y: rect.bottom },
      actions: [
        ...(invoice.documentUrl ? [{ label: 'Open PDF', onClick: () => window.open(`/api/inventory/invoices/${invoice.id}/document`, '_blank', 'noopener') }] : []),
        { label: invoice.documentUrl ? 'Replace PDF' : 'Attach PDF', onClick: () => document.getElementById(`invoice-file-${invoice.id}`)?.click() },
        { label: 'Edit number', onClick: () => { setEditingId(invoice.id); setEditNumber(invoice.number); } },
      ],
    });
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-4 pb-3">
        <p className="text-[#8E8E8E]" style={TEXT_14}>Every credit grant should point at the invoice that paid for it.</p>
        <button type="button" onClick={() => setIsFormOpen((open) => !open)} className={secondaryButtonClass} style={TEXT_15}>
          {isFormOpen ? 'Cancel' : <><Plus size={16} /> Add invoice</>}
        </button>
      </div>

      {isFormOpen ? (
        <div className="mb-4 rounded-[16px] bg-[#1B1B1B] p-4">
          <div className="grid grid-cols-2 gap-3">
            {([
              ['Invoice number', number, setNumber, 'text'],
              ['Reference', reference, setReference, 'text'],
              ['Purchase order', purchaseOrder, setPurchaseOrder, 'text'],
              ['Total amount', totalAmount, setTotalAmount, 'text'],
              ['Issue date', issueDate, setIssueDate, 'date'],
              ['Due date', dueDate, setDueDate, 'date'],
              ['Currency', currency, setCurrency, 'text'],
              ['Document URL', documentUrl, setDocumentUrl, 'text'],
            ] as const).map(([label, value, setValue, type]) => (
              <label key={label} className="flex flex-col gap-1.5">
                <span className="text-[#8E8E8E]" style={TEXT_14}>{label}</span>
                <input className={inputClass} style={TEXT_15} type={type} value={value} onChange={(e) => setValue(e.target.value)} />
              </label>
            ))}
          </div>
          <div className="mt-4 flex justify-end">
            <button type="button" onClick={handleCreate} disabled={isSubmitting} className={primaryButtonClass} style={TEXT_15}>
              {isSubmitting ? <Loader2 size={15} className="animate-spin" /> : null} Save invoice
            </button>
          </div>
        </div>
      ) : null}

      {isLoading ? (
        <p className="py-3 text-[#8E8E8E]" style={TEXT_15}>Loading invoices…</p>
      ) : invoices.length === 0 ? (
        <p className="py-3 text-[#8E8E8E]" style={TEXT_15}>No invoice recorded for this organisation yet.</p>
      ) : (
        <div className="divide-y divide-white/[0.08]">
          {invoices.map((invoice) => {
            const grantedCredits = invoice.grants.reduce((sum, grant) => sum + grant.includedCredits, 0);
            const busy = busyInvoiceId === invoice.id;
            return (
              <div key={invoice.id} className="flex min-h-[60px] items-center gap-4 py-3">
                <div className="min-w-0 flex-1">
                  {editingId === invoice.id ? (
                    <span className="flex items-center gap-2">
                      <input className={inputClass} style={TEXT_15} value={editNumber} onChange={(e) => setEditNumber(e.target.value)} aria-label="Invoice number" autoFocus />
                      <button type="button" className={primaryButtonClass} style={TEXT_15} disabled={busy} onClick={() => void handleRename(invoice.id)}>Save</button>
                      <button type="button" className={ghostButtonClass} style={TEXT_15} onClick={() => setEditingId(null)}>Cancel</button>
                    </span>
                  ) : (
                    <div className="overflow-hidden text-ellipsis whitespace-nowrap text-[#ECECEC]" style={TEXT_15}>{invoice.reference || invoice.number}</div>
                  )}
                  <div className="text-[#8E8E8E]" style={TEXT_14}>
                    {[
                      invoice.reference ? invoice.number : null,
                      `Issued ${formatDay(invoice.issueDate)}`,
                      invoice.purchaseOrder ? `PO ${invoice.purchaseOrder}` : null,
                      `${formatCreditsInteger(grantedCredits)} credits linked`,
                      invoice.documentUrl ? null : 'No PDF',
                    ].filter(Boolean).join(' · ')}
                  </div>
                  {invoice.grants.map((grant) => (
                    <div key={grant.id} className="text-[#8E8E8E]" style={TEXT_14}>
                      {grant.name || 'Unnamed grant'} · {formatCreditsInteger(grant.includedCredits)} credits · {formatDay(grant.activationDate)}
                    </div>
                  ))}
                </div>
                <span className="shrink-0 tabular-nums text-[#ECECEC]" style={TEXT_15}>{formatMoney(invoice.totalAmount, invoice.currency)}</span>
                <button
                  type="button"
                  onClick={(event) => openInvoiceMenu(event, invoice)}
                  aria-label="Invoice actions"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[#B4B4B4] transition-colors hover:bg-[#212121] hover:text-white"
                >
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <MoreHorizontal size={18} />}
                </button>
                <input
                  id={`invoice-file-${invoice.id}`}
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  disabled={busy}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = '';
                    if (file) void handleUpload(invoice.id, file);
                  }}
                />
              </div>
            );
          })}
        </div>
      )}

      {unlinkedGrants.length > 0 && invoices.length > 0 ? (
        <div className="mt-8">
          <h3 className="pb-1 text-[#8E8E8E]" style={TEXT_14}>Grants with no invoice</h3>
          <div className="divide-y divide-white/[0.08]">
            {unlinkedGrants.map((grant) => (
              <div key={grant.id} className="flex min-h-[52px] items-center justify-between gap-4 py-2">
                <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-[#ECECEC]" style={TEXT_15}>
                  {grant.name || `Grant of ${formatCreditsInteger(grant.includedCredits)} credits`}
                  {grant.activationDate ? <span className="text-[#8E8E8E]" style={{ fontSize: 'inherit' }}> · {formatDay(grant.activationDate)}</span> : null}
                </span>
                <select
                  className={selectClass}
                  style={TEXT_15}
                  defaultValue=""
                  disabled={linkingPlanId === grant.id}
                  onChange={(event) => handleLink(grant.id, event.target.value)}
                  aria-label="Link this grant to an invoice"
                >
                  <option value="">Link to…</option>
                  {invoices.map((invoice) => (
                    <option key={invoice.id} value={invoice.id}>{invoice.reference || invoice.number}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default React.memo(InvoicesSection);
